import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare, Plus, Flag, Upload, X, Image as ImageIcon, Search, Smile, ArrowLeft, Bold, Italic, Link2, Quote, ThumbsUp, Filter, Trash2, Info } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { z } from "zod";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import DOMPurify from "dompurify";
interface ForumCategory {
  id: string;
  name: string;
  description: string;
  display_order: number;
}
interface ForumThread {
  id: string;
  title: string;
  category_id: string;
  user_id: string;
  view_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  is_feature_request: boolean;
  vote_count: number;
  status: string;
  rejection_reason?: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string;
    email: string;
    display_name: string;
  };
  forum_categories?: {
    name: string;
  };
  post_count?: number;
  latest_post?: string;
  user_has_voted?: boolean;
}
interface ForumPost {
  id: string;
  content: string;
  thread_id: string;
  user_id: string;
  images: string[];
  is_reported: boolean;
  created_at: string;
  profiles?: {
    full_name: string;
    email: string;
    display_name: string;
  };
}
const BAD_WORDS = ['badword1', 'badword2', 'offensive', 'inappropriate'];
const filterBadWords = (text: string): string => {
  let filtered = text;
  BAD_WORDS.forEach(word => {
    const regex = new RegExp(word, 'gi');
    filtered = filtered.replace(regex, '***');
  });
  return filtered;
};
const forumThreadSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200, 'Title must be less than 200 characters').trim(),
  content: z.string().min(10, 'Content must be at least 10 characters').max(10000, 'Content must be less than 10,000 characters').trim(),
  category_id: z.string().uuid('Please select a category')
});
export default function Forum() {
  const {
    toast
  } = useToast();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [selectedThread, setSelectedThread] = useState<ForumThread | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showRulesDialog, setShowRulesDialog] = useState(false);
  const [hasAcceptedRules, setHasAcceptedRules] = useState(false);
  const [isCreateThreadOpen, setIsCreateThreadOpen] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportPostId, setReportPostId] = useState<string>("");
  const [reportReason, setReportReason] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [displayNameDialog, setDisplayNameDialog] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [filterMode, setFilterMode] = useState<"all" | "my-threads" | "my-posts">("all");
  const [quotedPost, setQuotedPost] = useState<ForumPost | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [isSiteAdmin, setIsSiteAdmin] = useState(false);
  const [userVotesRemaining, setUserVotesRemaining] = useState(10);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [newThread, setNewThread] = useState({
    title: "",
    content: "",
    category_id: ""
  });
  const [newPost, setNewPost] = useState({
    content: ""
  });
  useEffect(() => {
    checkRulesAcceptance();
    fetchCategories();
    fetchThreads();
    checkSiteAdmin();
    fetchUserVotesRemaining();
  }, []);
  useEffect(() => {
    if (selectedThread) {
      fetchPostsForThread(selectedThread.id);
    }
  }, [selectedThread]);
  const checkSiteAdmin = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    const {
      data: roleData
    } = await supabase.from('user_roles').select('role').eq('user_id', user.id).eq('role', 'site_admin').maybeSingle();
    setIsSiteAdmin(!!roleData);
  };
  
  const fetchUserVotesRemaining = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    
    const {
      data: votesData
    } = await supabase.from('forum_thread_votes').select('*', {
      count: 'exact',
      head: true
    }).eq('user_id', user.id);
    
    const usedVotes = (votesData as any)?.count || 0;
    setUserVotesRemaining(10 - usedVotes);
  };
  
  const checkRulesAcceptance = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    const {
      data: rulesData
    } = await supabase.from('forum_rules_acceptance').select('*').eq('user_id', user.id).maybeSingle();
    if (!rulesData) {
      setShowRulesDialog(true);
      return;
    }
    setHasAcceptedRules(true);

    // Check if user has display name
    const {
      data: profileData
    } = await supabase.from('profiles').select('display_name').eq('id', user.id).single();
    if (!profileData?.display_name) {
      setDisplayNameDialog(true);
    }
  };
  const acceptRules = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    const {
      error
    } = await supabase.from('forum_rules_acceptance').insert({
      user_id: user.id
    });
    if (error) {
      toast({
        title: "Error",
        description: "Failed to accept rules. Please try again.",
        variant: "destructive"
      });
      return;
    }
    setHasAcceptedRules(true);
    setShowRulesDialog(false);

    // Check if user has display name
    const {
      data: profileData
    } = await supabase.from('profiles').select('display_name').eq('id', user.id).single();
    if (!profileData?.display_name) {
      setDisplayNameDialog(true);
    } else {
      toast({
        title: "Welcome to the Forum!",
        description: "You can now participate in discussions."
      });
    }
  };
  const saveDisplayName = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    if (displayName.length < 3) {
      toast({
        title: "Invalid Display Name",
        description: "Display name must be at least 3 characters",
        variant: "destructive"
      });
      return;
    }
    const {
      error
    } = await supabase.from('profiles').update({
      display_name: displayName
    }).eq('id', user.id);
    if (error) {
      if (error.code === '23505') {
        toast({
          title: "Display Name Taken",
          description: "This display name is already in use. Please choose another.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to set display name. Please try again.",
          variant: "destructive"
        });
      }
      return;
    }
    setDisplayNameDialog(false);
    toast({
      title: "Display Name Set!",
      description: "Your display name has been saved."
    });
  };
  const fetchCategories = async () => {
    const {
      data,
      error
    } = await supabase.from('forum_categories').select('*').order('display_order', {
      ascending: true
    });
    if (!error && data) {
      setCategories(data);
    }
  };
  const fetchThreads = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    const {
      data: threadsData,
      error
    } = await supabase.from('forum_threads').select('*').order('is_pinned', {
      ascending: false
    }).order('created_at', {
      ascending: false
    });
    if (!error && threadsData) {
      const userIds = [...new Set(threadsData.map(t => t.user_id))];
      const {
        data: profilesData
      } = await supabase.from('profiles').select('id, full_name, email, display_name').in('id', userIds);
      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
      const categoryIds = [...new Set(threadsData.map(t => t.category_id))];
      const {
        data: categoriesData
      } = await supabase.from('forum_categories').select('id, name').in('id', categoryIds);
      const categoriesMap = new Map(categoriesData?.map(c => [c.id, c]) || []);

      // Fetch user votes if logged in
      let votesMap = new Map();
      if (user) {
        const {
          data: votesData
        } = await supabase.from('forum_thread_votes').select('thread_id').eq('user_id', user.id);
        votesMap = new Map(votesData?.map(v => [v.thread_id, true]) || []);
      }
      const enrichedThreads = await Promise.all(threadsData.map(async thread => {
        const {
          count
        } = await supabase.from('forum_posts').select('*', {
          count: 'exact',
          head: true
        }).eq('thread_id', thread.id);
        const {
          data: latestPost
        } = await supabase.from('forum_posts').select('created_at').eq('thread_id', thread.id).order('created_at', {
          ascending: false
        }).limit(1).maybeSingle();
        return {
          ...thread,
          profiles: profilesMap.get(thread.user_id),
          forum_categories: categoriesMap.get(thread.category_id),
          post_count: count || 0,
          latest_post: latestPost?.created_at,
          user_has_voted: votesMap.get(thread.id) || false
        };
      }));
      setThreads(enrichedThreads);
    }
  };
  const fetchPostsForThread = async (threadId: string) => {
    const {
      data: postsData,
      error
    } = await supabase.from('forum_posts').select('*').eq('thread_id', threadId).eq('is_hidden', false).order('created_at', {
      ascending: false
    });
    if (!error && postsData) {
      const userIds = [...new Set(postsData.map(p => p.user_id))];
      const {
        data: profilesData
      } = await supabase.from('profiles').select('id, full_name, email, display_name').in('id', userIds);
      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);
      const enrichedPosts = postsData.map(post => ({
        ...post,
        profiles: profilesMap.get(post.user_id)
      }));
      setPosts(enrichedPosts as any);
    }
  };
  const handleCreateThread = async (e: React.FormEvent) => {
    e.preventDefault();
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    const result = forumThreadSchema.safeParse(newThread);
    if (!result.success) {
      toast({
        title: 'Validation Error',
        description: result.error.errors[0].message,
        variant: 'destructive'
      });
      return;
    }
    const filteredTitle = filterBadWords(newThread.title);
    const filteredContent = filterBadWords(newThread.content);

    // Check if this is a feature request category
    const selectedCat = categories.find(c => c.id === newThread.category_id);
    const isFeatureRequest = selectedCat?.name === 'Feature Requests';
    const {
      data: threadData,
      error: threadError
    } = await supabase.from('forum_threads').insert({
      title: filteredTitle,
      category_id: newThread.category_id,
      user_id: user.id,
      is_feature_request: isFeatureRequest
    } as any).select().single();
    if (threadError || !threadData) {
      toast({
        title: "Error",
        description: "Failed to create thread. Please try again.",
        variant: "destructive"
      });
      return;
    }
    const {
      error: postError
    } = await supabase.from('forum_posts').insert({
      thread_id: threadData.id,
      title: filteredTitle,
      content: filteredContent,
      user_id: user.id,
      images: []
    } as any);
    if (postError) {
      toast({
        title: "Error",
        description: "Failed to create initial post. Please try again.",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "Success",
      description: isFeatureRequest ? "Feature request submitted successfully!" : "Thread created successfully!"
    });
    setNewThread({
      title: "",
      content: "",
      category_id: ""
    });
    setIsCreateThreadOpen(false);
    fetchThreads();
  };
  const handleCreatePost = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user || !selectedThread) return;
    if (newPost.content.length < 10) {
      toast({
        title: "Validation Error",
        description: "Post content must be at least 10 characters",
        variant: "destructive"
      });
      return;
    }
    const filteredContent = filterBadWords(newPost.content);

    // Handle image uploads
    const imageUrls: string[] = [];
    for (const file of uploadedImages) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Image too large",
          description: "Images must be under 10MB",
          variant: "destructive"
        });
        return;
      }
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `forum/${user.id}/${fileName}`;
      const {
        error: uploadError
      } = await supabase.storage.from('fundraising-images').upload(filePath, file);
      if (uploadError) {
        toast({
          title: "Error",
          description: "Failed to upload image",
          variant: "destructive"
        });
        return;
      }
      const {
        data: {
          publicUrl
        }
      } = supabase.storage.from('fundraising-images').getPublicUrl(filePath);
      imageUrls.push(publicUrl);
    }
    const {
      error
    } = await supabase.from('forum_posts').insert({
      thread_id: selectedThread.id,
      title: `Re: ${selectedThread.title}`,
      content: filteredContent,
      user_id: user.id,
      images: imageUrls
    } as any);
    if (error) {
      toast({
        title: "Error",
        description: "Failed to create post. Please try again.",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "Success",
      description: "Reply added successfully!"
    });
    setNewPost({
      content: ""
    });
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
    setUploadedImages([]);
    setQuotedPost(null);
    setShowEmojiPicker(false);
    fetchPostsForThread(selectedThread.id);
  };
  const handleReportPost = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    const {
      error
    } = await supabase.from('forum_post_reports').insert({
      post_id: reportPostId,
      reported_by: user.id,
      reason: reportReason
    });
    if (error) {
      toast({
        title: "Error",
        description: "Failed to report post. Please try again.",
        variant: "destructive"
      });
      return;
    }
    await supabase.from('forum_posts').update({
      is_reported: true
    }).eq('id', reportPostId);
    toast({
      title: "Success",
      description: "Post reported to administrators."
    });
    setIsReportDialogOpen(false);
    setReportReason("");
    setReportPostId("");
    if (selectedThread) {
      fetchPostsForThread(selectedThread.id);
    }
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const isValid = file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/jpg';
      if (!isValid) {
        toast({
          title: "Invalid file type",
          description: "Only PNG and JPG images are allowed",
          variant: "destructive"
        });
      }
      return isValid;
    });
    setUploadedImages(prev => [...prev, ...validFiles]);
  };
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const editor = editorRef.current;
    if (editor) {
      editor.innerHTML += emojiData.emoji;
      const markdownContent = htmlToMarkdown(editor.innerHTML);
      setNewPost({ content: markdownContent });
    }
    setShowEmojiPicker(false);
  };
  const handleQuote = (post: ForumPost) => {
    const userName = post.profiles?.display_name || post.profiles?.full_name || 'Someone';
    const quotedText = `> ${userName} said:\n> ${post.content.replace(/\n/g, '\n> ')}\n\n`;
    const editor = editorRef.current;
    if (editor) {
      const htmlQuote = `<blockquote style="border-left: 3px solid hsl(var(--primary)); padding-left: 12px; margin: 8px 0; color: hsl(var(--muted-foreground));">${userName} said:<br>${post.content.replace(/\n/g, '<br>')}</blockquote><br>`;
      editor.innerHTML = htmlQuote + editor.innerHTML;
      setNewPost({
        content: quotedText + newPost.content
      });
    }
    setQuotedPost(post);
    editorRef.current?.focus();
  };
  const handleDeletePost = async (postId: string, postUserId: string) => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;

    // Check if user is the post owner or site admin
    if (user.id !== postUserId && !isSiteAdmin) {
      toast({
        title: "Permission Denied",
        description: "You can only delete your own posts",
        variant: "destructive"
      });
      return;
    }
    const {
      error
    } = await supabase.from('forum_posts').delete().eq('id', postId);
    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "Success",
      description: "Post deleted successfully"
    });
    if (selectedThread) {
      fetchPostsForThread(selectedThread.id);
    }
  };
  const handleDeleteThread = async (thread: ForumThread) => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;

    // Check post count - only allow deletion if there's only 1 post (the initial post) or user is site admin
    if (thread.post_count && thread.post_count > 1 && !isSiteAdmin) {
      toast({
        title: "Cannot Delete Thread",
        description: "This thread has replies and can only be deleted by site administrators",
        variant: "destructive"
      });
      return;
    }

    // Check if user is the thread owner or site admin
    if (user.id !== thread.user_id && !isSiteAdmin) {
      toast({
        title: "Permission Denied",
        description: "You can only delete your own threads",
        variant: "destructive"
      });
      return;
    }

    // Delete all posts first
    await supabase.from('forum_posts').delete().eq('thread_id', thread.id);

    // Delete the thread
    const {
      error
    } = await supabase.from('forum_threads').delete().eq('id', thread.id);
    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete thread",
        variant: "destructive"
      });
      return;
    }
    toast({
      title: "Success",
      description: "Thread deleted successfully"
    });
    fetchThreads();
  };
  const handleVote = async (threadId: string, hasVoted: boolean) => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) return;
    if (hasVoted) {
      // Remove vote
      const {
        error
      } = await supabase.from('forum_thread_votes').delete().eq('thread_id', threadId).eq('user_id', user.id);
      if (error) {
        toast({
          title: "Error",
          description: "Failed to remove vote",
          variant: "destructive"
        });
        return;
      }

      // Update vote count
      await supabase.rpc('decrement_vote_count', {
        thread_id: threadId
      } as any);
    } else {
      // Check vote limit
      const {
        data: votesData
      } = await supabase.from('forum_thread_votes').select('*', {
        count: 'exact',
        head: true
      }).eq('user_id', user.id);
      if ((votesData as any)?.count >= 10) {
        toast({
          title: "Vote Limit Reached",
          description: "You've used all 10 votes. Wait for ideas to be marked as planned/completed to get votes back.",
          variant: "destructive"
        });
        return;
      }

      // Add vote
      const {
        error
      } = await supabase.from('forum_thread_votes').insert({
        thread_id: threadId,
        user_id: user.id
      } as any);
      if (error) {
        toast({
          title: "Error",
          description: "Failed to add vote",
          variant: "destructive"
        });
        return;
      }

      // Update vote count
      await supabase.rpc('increment_vote_count', {
        thread_id: threadId
      } as any);
    }
    fetchThreads();
  };
  // Convert HTML content to markdown for storage
  const htmlToMarkdown = (html: string): string => {
    return html
      .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
      .replace(/<b>(.*?)<\/b>/g, '**$1**')
      .replace(/<em>(.*?)<\/em>/g, '*$1*')
      .replace(/<i>(.*?)<\/i>/g, '*$1*')
      .replace(/<a href="(.*?)".*?>(.*?)<\/a>/g, '[$2]($1)')
      .replace(/<br\s*\/?>/g, '\n')
      .replace(/<div>(.*?)<\/div>/g, '$1\n')
      .replace(/<\/div>/g, '\n')
      .replace(/&nbsp;/g, ' ');
  };
  
  // Function to wrap emoji characters in spans for hover effect
  const wrapEmojis = (text: string): string => {
    // Regex to match emoji characters
    const emojiRegex = /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;
    return text.replace(emojiRegex, '<span class="emoji-char">$1</span>');
  };

  const applyFormatting = (format: 'bold' | 'italic' | 'link') => {
    const editor = editorRef.current;
    if (!editor) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      toast({
        title: "No Text Selected",
        description: "Please select some text to format",
        variant: "destructive"
      });
      return;
    }

    const selectedText = selection.toString();
    if (!selectedText) {
      toast({
        title: "No Text Selected",
        description: "Please select some text to format",
        variant: "destructive"
      });
      return;
    }

    switch (format) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      case 'italic':
        document.execCommand('italic', false);
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          document.execCommand('createLink', false, url);
        }
        break;
    }

    // Update state with the new HTML content
    const htmlContent = editor.innerHTML;
    const markdownContent = htmlToMarkdown(htmlContent);
    setNewPost({ content: markdownContent });
    
    editor.focus();
  };

  // Get current user for filtering
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({
      data: {
        user
      }
    }) => {
      setCurrentUserId(user?.id || null);
    });
  }, []);
  const filteredThreads = threads.filter(thread => {
    // Filter by mode
    if (filterMode === 'my-threads' && thread.user_id !== currentUserId) return false;
    if (filterMode === 'my-posts') {
      const hasUserPost = posts.some(p => p.user_id === currentUserId && p.thread_id === thread.id);
      if (!hasUserPost) return false;
    }
    const matchesCategory = selectedCategory === "" || thread.category_id === selectedCategory;
    const matchesSearch = searchQuery === "" || thread.title.toLowerCase().includes(searchQuery.toLowerCase()) || thread.profiles?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || thread.profiles?.display_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  if (!hasAcceptedRules) {
    return <AlertDialog open={showRulesDialog} onOpenChange={setShowRulesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Welcome to the Community Forum!</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>Before you can participate, please read and accept our forum rules:</p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li>Be kind and respectful to all members</li>
                <li>No offensive, abusive, or discriminatory language</li>
                <li>Stay on topic and contribute constructively</li>
                <li>Respect others' privacy and do not share personal information</li>
                <li>No spam, advertising, or self-promotion without permission</li>
                <li>Report inappropriate content to administrators</li>
                <li>Images must be appropriate and under 10MB (PNG/JPG only)</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={acceptRules}>I Accept the Rules</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>;
  }

  // Display name dialog
  if (displayNameDialog) {
    return <AlertDialog open={displayNameDialog} onOpenChange={setDisplayNameDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Choose Your Display Name</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-4">This is the name that will appear on your posts and threads. It must be unique across the platform.</p>
              <Input placeholder="Enter display name..." value={displayName} onChange={e => setDisplayName(e.target.value)} className="mb-2" />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={saveDisplayName}>Set Display Name</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>;
  }
  if (selectedThread) {
    return <div className="container mx-auto py-8 space-y-6">
        <Button variant="ghost" onClick={() => setSelectedThread(null)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Threads
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{selectedThread.title}</CardTitle>
                <CardDescription>
                  Started by {selectedThread.profiles?.display_name || selectedThread.profiles?.full_name || 'Unknown'} in {selectedThread.forum_categories?.name || 'Unknown'}
                </CardDescription>
              </div>
              {selectedThread.is_feature_request && <div className="flex items-center gap-2">
                  <ThumbsUp className="h-5 w-5" />
                  <span className="font-semibold">{selectedThread.vote_count} votes</span>
                  {selectedThread.status !== 'pending' && <span className={`px-2 py-1 rounded text-xs ${
                    selectedThread.status === 'added' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 
                    selectedThread.status === 'planned' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    selectedThread.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}>
                      {selectedThread.status}
                    </span>}
                </div>}
            </div>
            {selectedThread.is_feature_request && selectedThread.status === 'rejected' && selectedThread.rejection_reason && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Rejection Reason:</p>
                <p className="text-sm text-red-700 dark:text-red-300">{selectedThread.rejection_reason}</p>
              </div>
            )}
          </CardHeader>
        </Card>

        <div className="space-y-4">
          {posts.map(post => <Card key={post.id}>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="text-sm text-muted-foreground">
                    {post.profiles?.display_name || post.profiles?.full_name || 'Unknown'} • {new Date(post.created_at).toLocaleDateString()}
                  </div>
                  <div className="flex gap-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => handleQuote(post)}>
                            <Quote className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Quote this post in your reply</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {(currentUserId === post.user_id || isSiteAdmin) && <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => handleDeletePost(post.id, post.user_id)} className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Delete this post</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={e => {
                        e.stopPropagation();
                        setReportPostId(post.id);
                        setIsReportDialogOpen(true);
                      }} disabled={post.is_reported}>
                            <Flag className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Report this post for violating community guidelines</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
                <div 
                  className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      wrapEmojis(
                        post.content
                          .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.+?)\*/g, '<em>$1</em>')
                          .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary underline" target="_blank" rel="noopener noreferrer">$1</a>')
                      ),
                      {
                        ALLOWED_TAGS: ['strong', 'em', 'a', 'br', 'p', 'span'],
                        ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
                      }
                    )
                  }}
                />
                {post.images && post.images.length > 0 && <div className="flex flex-wrap gap-2">
                    {post.images.map((img, idx) => <img 
                      key={idx} 
                      src={img} 
                      alt="Post image" 
                      className="h-32 w-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity" 
                      onClick={() => setLightboxImage(img)}
                    />)}
                  </div>}
              </CardContent>
            </Card>)}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Add a Reply</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex gap-2 mb-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" variant="outline" size="sm" onClick={() => applyFormatting('bold')}>
                        <Bold className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Bold (select text first)</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" variant="outline" size="sm" onClick={() => applyFormatting('italic')}>
                        <Italic className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Italic (select text first)</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" variant="outline" size="sm" onClick={() => applyFormatting('link')}>
                        <Link2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Add link (select text first)</p></TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                  <Smile className="h-4 w-4" />
                </Button>
              </div>
              <div className="relative">
                <div
                  ref={editorRef}
                  contentEditable
                  className="min-h-[150px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 prose prose-sm max-w-none dark:prose-invert [&_strong]:font-bold [&_em]:italic [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground"
                  onInput={(e) => {
                    const htmlContent = e.currentTarget.innerHTML;
                    const markdownContent = htmlToMarkdown(htmlContent);
                    setNewPost({ content: markdownContent });
                    // Update placeholder visibility
                    if (htmlContent === '' || htmlContent === '<br>') {
                      e.currentTarget.setAttribute('data-placeholder', 'Write your reply...');
                    } else {
                      e.currentTarget.removeAttribute('data-placeholder');
                    }
                  }}
                  style={{
                    minHeight: '150px',
                  }}
                  data-placeholder="Write your reply..."
                />
                {showEmojiPicker && <div className="absolute bottom-12 right-0 z-50">
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Images (Optional, max 10MB each)</Label>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" onClick={() => document.getElementById('post-image-upload')?.click()}>
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Upload Images
                </Button>
                <input id="post-image-upload" type="file" accept="image/png,image/jpeg,image/jpg" multiple className="hidden" onChange={handleImageUpload} />
              </div>
              {uploadedImages.length > 0 && <div className="flex flex-wrap gap-2 mt-2">
                  {uploadedImages.map((file, idx) => <div key={idx} className="relative">
                      <img src={URL.createObjectURL(file)} alt="Preview" className="h-20 w-20 object-cover rounded" />
                      <button type="button" onClick={() => setUploadedImages(prev => prev.filter((_, i) => i !== idx))} className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1">
                        <X className="h-3 w-3" />
                      </button>
                    </div>)}
                </div>}
            </div>
            <Button onClick={handleCreatePost}>Post Reply</Button>
          </CardContent>
        </Card>

        {/* Report Post Dialog */}
        <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Report Post</DialogTitle>
              <DialogDescription>Please provide a reason for reporting this post</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Textarea placeholder="Explain why you're reporting this post..." value={reportReason} onChange={e => setReportReason(e.target.value)} rows={4} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsReportDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleReportPost}>Submit Report</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Lightbox */}
        <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Image Preview</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center">
              <img src={lightboxImage || ""} alt="Full size" className="max-w-full max-h-[70vh] object-contain" />
            </div>
          </DialogContent>
        </Dialog>
      </div>;
  }
  const selectedCat = categories.find(c => c.id === newThread.category_id);
  const isFeatureRequest = selectedCat?.name === 'Feature Requests';

  // Check if current selected category is Feature Requests
  const currentCategory = categories.find(c => c.id === selectedCategory);
  const isInFeatureRequestSection = currentCategory?.name === 'Feature Requests';
  return <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Community Forum</h1>
          <p className="text-muted-foreground mt-2">Connect and share with other PTA members</p>
        </div>
        <Dialog open={isCreateThreadOpen} onOpenChange={open => {
        setIsCreateThreadOpen(open);
        // Pre-select the currently selected category when opening dialog
        if (open && selectedCategory && newThread.category_id === "") {
          setNewThread({
            ...newThread,
            category_id: selectedCategory
          });
        }
      }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              {isInFeatureRequestSection ? 'Submit Feature Request' : 'New Thread'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{isFeatureRequest ? 'Submit Feature Request' : 'Create New Thread'}</DialogTitle>
              <DialogDescription>
                {isFeatureRequest ? 'Share your idea for improving the platform' : 'Start a new discussion in the community'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateThread} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Select value={newThread.category_id} onValueChange={value => setNewThread({
                ...newThread,
                category_id: value
              })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="title">{isFeatureRequest ? 'Request Title' : 'Thread Title'}</Label>
                <Input id="title" value={newThread.title} onChange={e => setNewThread({
                ...newThread,
                title: e.target.value
              })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">{isFeatureRequest ? 'Description' : 'Initial Post'}</Label>
                <Textarea id="content" value={newThread.content} onChange={e => setNewThread({
                ...newThread,
                content: e.target.value
              })} rows={6} required />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateThreadOpen(false)}>Cancel</Button>
                <Button type="submit">{isFeatureRequest ? 'Submit Request' : 'Create Thread'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search threads..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <div className="flex gap-2">
          
          <Button variant={filterMode === "my-threads" ? "default" : "outline"} onClick={() => setFilterMode("my-threads")}>
            My Threads
          </Button>
          <Button variant={filterMode === "my-posts" ? "default" : "outline"} onClick={() => setFilterMode("my-posts")}>
            My Posts
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        <Card className="w-64 shrink-0">
          <CardHeader>
            <CardTitle>Categories</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {categories.map(cat => <Button key={cat.id} variant={selectedCategory === cat.id ? "default" : "ghost"} className="w-full justify-start" onClick={() => {
            setSelectedCategory(cat.id);
            // Also pre-select this category when opening the create thread dialog
            if (newThread.category_id === "") {
              setNewThread({
                ...newThread,
                category_id: cat.id
              });
            }
          }}>
                {cat.name}
              </Button>)}
          </CardContent>
        </Card>

        <div className="flex-1 space-y-4">
          {isInFeatureRequestSection && (
            <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                  <div className="space-y-2">
                    <h3 className="font-semibold text-blue-900 dark:text-blue-100">How Feature Requests Work</h3>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Submit ideas to improve our platform and vote on requests from others. Each user has <strong>10 votes</strong> to distribute across feature requests. 
                      You have <strong>{userVotesRemaining} votes remaining</strong>. Click on a request to vote or remove your vote. 
                      High-voted features are prioritized for development.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {filteredThreads.length === 0 ? <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No threads yet. Be the first to start a discussion!</p>
              </CardContent>
            </Card> : filteredThreads.map(thread => <Card key={thread.id} className="cursor-pointer transition-colors" onClick={() => setSelectedThread(thread)}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <MessageSquare className="h-5 w-5" />
                        {thread.title}
                        {thread.status !== 'pending' && thread.is_feature_request && <span className={`px-2 py-1 rounded text-xs ml-2 ${
                          thread.status === 'added' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 
                          thread.status === 'planned' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                          thread.status === 'rejected' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                        }`}>
                            {thread.status}
                          </span>}
                      </CardTitle>
                      <CardDescription>
                        Started by {thread.profiles?.display_name || thread.profiles?.full_name || 'Unknown'} in {thread.forum_categories?.name || 'Unknown'}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {thread.is_feature_request && <Button variant={thread.user_has_voted ? "default" : "outline"} size="sm" onClick={e => {
                  e.stopPropagation();
                  handleVote(thread.id, thread.user_has_voted || false);
                }}>
                          <ThumbsUp className="h-4 w-4 mr-2" />
                          {thread.vote_count}
                        </Button>}
                      {(currentUserId === thread.user_id || isSiteAdmin) && <Button variant="ghost" size="sm" onClick={e => {
                  e.stopPropagation();
                  handleDeleteThread(thread);
                }} className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{thread.post_count} {thread.post_count === 1 ? 'reply' : 'replies'}</span>
                    <span>Last activity: {thread.latest_post ? new Date(thread.latest_post).toLocaleDateString() : new Date(thread.created_at).toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>)}
        </div>
      </div>
    </div>;
}